import { create } from "zustand";

// Phases of the VLA In-Use (deal) lock for the deal currently open (OR-20985):
//   acquiring     — request in flight; treat as read-only until we know we hold it
//   held-by-me    — current user owns the lock; the deal is editable
//   held-by-other — another analyst holds it; read-only + show the lock banner
//   error         — acquire failed for a non-409 reason; fail-safe to read-only
export type VlaLockPhase = "acquiring" | "held-by-me" | "held-by-other" | "error";

export interface VlaLockState {
  phase: VlaLockPhase;
  /** SSO user id of the holder, when the deal is held by another user. */
  holderId?: string;
  /** Title-cased display name of the holder, read from the GET response's `fullName` field. */
  holderName?: string;
}

interface ApprovalState {
  /** The approval/VLA id whose lock the `lock` state describes. */
  approvalId?: string;
  lock: VlaLockState;
  startAcquiring: (approvalId: string) => void;
  setHeldByMe: () => void;
  setHeldByOther: (holder: { holderId?: string; holderName?: string }) => void;
  setError: () => void;
  reset: () => void;
}

// Default to `acquiring` (read-only) so the deal is never editable before the
// lock is confirmed in our name — the fail-safe direction for a concurrency guard.
const INITIAL_LOCK: VlaLockState = { phase: "acquiring" };

/**
 * Global store for the open deal's identity and VLA-lock state. Mirrors the
 * lock decision made once at deal entry (see `useVlaLock`) so any surface can
 * read read-only-ness without prop drilling. No polling — the entry-time
 * decision persists until the deal is exited.
 */
export const useApprovalStore = create<ApprovalState>((set) => ({
  approvalId: undefined,
  lock: INITIAL_LOCK,
  startAcquiring: (approvalId) => set({ approvalId, lock: { phase: "acquiring" } }),
  setHeldByMe: () => set({ lock: { phase: "held-by-me" } }),
  setHeldByOther: (holder) =>
    set({ lock: { phase: "held-by-other", holderId: holder.holderId, holderName: holder.holderName } }),
  setError: () => set({ lock: { phase: "error" } }),
  reset: () => set({ approvalId: undefined, lock: INITIAL_LOCK }),
}));

/**
 * True whenever the current user does NOT hold the lock. `acquiring`,
 * `held-by-other`, and `error` all render the deal read-only; only the
 * confirmed `held-by-me` phase unlocks editing.
 *
 * Named distinctly from `features/lock/ReadOnlyContext`'s `useReadOnly` (the
 * one most components should use, sourced from `ReviewPage`'s
 * `ReadOnlyProvider`) so the two can't be confused at the import line.
 * `ReviewPage` is the only caller that needs the raw store value, to seed
 * that Provider.
 *
 * NOTE: this drives UX only (disabled fieldset, hidden Save). It is NOT a
 * security boundary — a user could re-enable controls via devtools and still
 * fire writes. The concurrency guarantee must be enforced server-side: the BFF
 * is expected to reject writes from a non-lock-holder (403/409). Do not treat
 * this flag as the enforcement point.
 * @returns {boolean} Whether editable surfaces should be disabled.
 */
export function useLockReadOnly(): boolean {
  return useApprovalStore((state) => state.lock.phase !== "held-by-me");
}

/**
 * The current VLA-lock state (phase + holder), for the lock banner.
 * @returns {VlaLockState} The lock phase and, when held by another, the holder.
 */
export function useDealLock(): VlaLockState {
  return useApprovalStore((state) => state.lock);
}
